import React, { useState, useEffect } from 'react';
import {
    Table, Button, Modal, Select, Tag, Space, Input,
    message, Card, Row, Col, Statistic, Popconfirm, Tooltip
} from 'antd';
import {
    TrophyOutlined, PlusOutlined, StopOutlined, SearchOutlined
} from '@ant-design/icons';
import axios from '../../utils/axios';
import util from '../../utils/util';

export default function QoCFacilityNominations() {
    const [nominations, setNominations]   = useState([]);
    const [facilities, setFacilities]     = useState([]);
    const [categories, setCategories]     = useState([]);
    const [cycles, setCycles]             = useState([]);
    const [loading, setLoading]           = useState(false);
    const [nomModal, setNomModal]         = useState(false);
    const [selectedCycle, setSelectedCycle] = useState('ENHEA-2026');
    const [filterStatus, setFilterStatus]   = useState('active');
    const [search, setSearch]               = useState('');

    // Form state
    const [selFacilities, setSelFacilities] = useState([]);
    const [selCycle, setSelCycle]           = useState('ENHEA-2026');
    const [nomNotes, setNomNotes]           = useState('');

    const isAdmin = util.getModules()?.['ays_admin'] === 1 ||
                    parseInt(localStorage.getItem('is_admin') || '0') === 1 ||
                    parseInt(localStorage.getItem('is_national') || '0') === 1;

    useEffect(() => {
        loadNominations();
        loadFacilities();
        loadCategories();
        loadCycles();
    }, [selectedCycle]); // eslint-disable-line

    const loadNominations = () => {
        setLoading(true);
        axios.get('ays/ha/nominations', { params: { award_cycle: selectedCycle } })
            .then(r => setNominations(r.data?.data || []))
            .catch(() => setNominations([]))
            .finally(() => setLoading(false));
    };

    const loadFacilities = () => {
        import('../../services/FacilityService').then(({ default: FacilityService }) => {
            FacilityService.all().then(r => setFacilities(r.data?.result?.data || r.data?.data || []));
        });
    };

    const loadCategories = () => {
        import('../../services/AYSService').then(({ default: AYSService }) => {
            AYSService.getHaCategories().then(r => {
                setCategories((r.data?.data || []).filter(c =>
                    ['TERTIARY','REGIONAL','HEALTH_CENTRE','CLINIC','PATIENT_SAFETY'].includes(c.code)
                ));
            });
        });
    };

    const loadCycles = () => {
        import('../../services/QoCService').then(({ default: QoCService }) => {
            QoCService.getPeriods().then(r => {
                const enhea = (r.data?.data || []).filter(p => p.label.startsWith('ENHEA'));
                if (enhea.length) setCycles(enhea.map(p => p.label));
                else setCycles(['ENHEA-2026']);
            });
        });
    };

    const nominate = async () => {
        if (!selFacilities.length) { message.warning('Select at least one facility'); return; }
        util.showLoader();
        try {
            await Promise.all(selFacilities.map(fId =>
                axios.post('ays/ha/nominations/save', {
                    facility_id: fId,
                    award_cycle: selCycle,
                    notes:       nomNotes,
                })
            ));
            message.success(`${selFacilities.length} facility/facilities nominated`);
            setNomModal(false);
            setSelFacilities([]);
            setNomNotes('');
            loadNominations();
        } catch(e) { message.error(e.message); }
        finally { util.hideLoader(); }
    };

    const withdraw = async (id) => {
        util.showLoader();
        try {
            await axios.post('ays/ha/nominations/withdraw', { id });
            message.success('Nomination withdrawn');
            loadNominations();
        } catch(e) { message.error(e.message); }
        finally { util.hideLoader(); }
    };

    const reinstate = async (id) => {
        util.showLoader();
        try {
            await axios.post('ays/ha/nominations/reinstate', { id });
            message.success('Nomination reinstated');
            loadNominations();
        } catch(e) { message.error(e.message); }
        finally { util.hideLoader(); }
    };

    // Already nominated facility IDs for current cycle
    const nominatedIds = nominations.filter(n => !n.withdrawn).map(n => String(n.facility_id));

    const filtered = nominations.filter(n => {
        if (filterStatus === 'active'    && n.withdrawn)  return false;
        if (filterStatus === 'withdrawn' && !n.withdrawn) return false;
        if (search && !n.facility_name?.toLowerCase().includes(search.toLowerCase())) return false;
        return true;
    });

    const cols = [
        { title: 'Facility', dataIndex: 'facility_name', render: (t, r) => (
            <div>
                <div style={{ fontWeight: 700 }}>{t}</div>
                <div style={{ fontSize: 11, color: '#888' }}>{r.site_type} · {r.region}</div>
            </div>
        )},
        { title: 'Award Category', dataIndex: 'category_name', width: 220,
            render: v => v ? <Tag color="gold">{v}</Tag> : <span style={{ color: '#ccc' }}>Not categorised</span>
        },
        { title: 'Nominated By', dataIndex: 'nominated_by_name', width: 160 },
        { title: 'Nominated', dataIndex: 'nominated_at', width: 120,
            render: v => v?.split(' ')[0]
        },
        { title: 'Status', width: 110, render: (_, r) => (
            r.withdrawn
                ? <Tag color="red">Withdrawn</Tag>
                : <Tag color="green"><TrophyOutlined /> Nominated</Tag>
        )},
        { title: '', width: 120, render: (_, r) => isAdmin ? (
            r.withdrawn ? (
                <Button size="small" type="primary" onClick={() => reinstate(r.id)}>
                    Reinstate
                </Button>
            ) : (
                <Popconfirm title="Withdraw this nomination?" onConfirm={() => withdraw(r.id)}
                    okText="Withdraw" okButtonProps={{ danger: true }}>
                    <Button size="small" danger icon={<StopOutlined />}>Withdraw</Button>
                </Popconfirm>
            )
        ) : null},
    ];

    return (
        <div className="page-content">
            <div className="page-head d-flex">
                <div className="my-auto">
                    <h4 style={{ margin: 0 }}>Facility Nominations</h4>
                    <small className="text-muted">Health Excellence Awards - Nominate facilities for evaluation</small>
                </div>
                <div className="my-auto ml-auto d-flex" style={{ gap: 8 }}>
                    <Select value={selectedCycle} onChange={v => setSelectedCycle(v)} style={{ width: 160 }}>
                        {cycles.map(c => <Select.Option key={c} value={c}>{c}</Select.Option>)}
                    </Select>
                    {isAdmin && (
                        <Button type="primary" icon={<PlusOutlined />} onClick={() => {
                            setSelCycle(selectedCycle);
                            setNomModal(true);
                        }}>
                            Nominate Facilities
                        </Button>
                    )}
                </div>
            </div>

            <div className="page-pad">
                {/* KPI cards */}
                <Row gutter={12} style={{ marginBottom: 20 }}>
                    {[
                        { label: 'Total Nominated',  value: nominations.filter(n => !n.withdrawn).length,  color: '#16a34a' },
                        { label: 'Withdrawn',        value: nominations.filter(n => n.withdrawn).length,   color: '#dc2626' },
                        ...categories.slice(0, 3).map(cat => ({
                            label: cat.name,
                            value: nominations.filter(n => !n.withdrawn && n.category_code === cat.code).length,
                            color: '#1890ff',
                        })),
                    ].map(k => (
                        <Col key={k.label} xs={12} sm={8} md={4}>
                            <Card size="small">
                                <Statistic title={k.label} value={k.value}
                                    valueStyle={{ color: k.color, fontWeight: 800, fontSize: 24 }} />
                            </Card>
                        </Col>
                    ))}
                </Row>

                {/* Filters */}
                <div style={{ display: 'flex', gap: 8, marginBottom: 16 }}>
                    <Input.Search placeholder="Search facility..." style={{ width: 260 }}
                        prefix={<SearchOutlined />}
                        value={search} onChange={e => setSearch(e.target.value)} allowClear />
                    <Select value={filterStatus} onChange={setFilterStatus} style={{ width: 140 }}>
                        <Select.Option value="all">All</Select.Option>
                        <Select.Option value="active">Nominated</Select.Option>
                        <Select.Option value="withdrawn">Withdrawn</Select.Option>
                    </Select>
                </div>

                <Table rowKey="id" dataSource={filtered} columns={cols}
                    loading={loading} size="small"
                    pagination={{ pageSize: 25 }}
                    locale={{ emptyText: 'No nominations yet for this cycle. Click "Nominate Facilities" to get started.' }} />
            </div>

            {/* Nomination modal */}
            <Modal title={`Nominate Facilities — ${selCycle}`}
                open={nomModal}
                onCancel={() => { setNomModal(false); setSelFacilities([]); setNomNotes(''); }}
                onOk={nominate}
                okText={`Nominate ${selFacilities.length > 0 ? `(${selFacilities.length})` : ''}`}
                width={620}>
                <div style={{ marginBottom: 16 }}>
                    <div style={{ fontWeight: 600, marginBottom: 6 }}>Award Cycle</div>
                    <Select value={selCycle} onChange={setSelCycle} style={{ width: '100%' }}>
                        {cycles.map(c => <Select.Option key={c} value={c}>{c}</Select.Option>)}
                    </Select>
                </div>
                <div style={{ marginBottom: 16 }}>
                    <div style={{ fontWeight: 600, marginBottom: 6 }}>Select Facilities to Nominate</div>
                    <Select mode="multiple" showSearch allowClear
                        placeholder="Search and select facilities..."
                        style={{ width: '100%' }}
                        filterOption={(input, opt) => opt.label?.toLowerCase().includes(input.toLowerCase())}
                        value={selFacilities}
                        onChange={setSelFacilities}
                        options={facilities
                            .filter(f => !nominatedIds.includes(String(f.id)))
                            .map(f => ({
                                value: f.id,
                                label: `${f.business_name} (${f.site_type || 'N/A'})`,
                            }))} />
                    <div style={{ fontSize: 11, color: '#888', marginTop: 4 }}>
                        Already-nominated facilities are hidden. Award category is assigned automatically based on facility type.
                    </div>
                </div>
                <div>
                    <div style={{ fontWeight: 600, marginBottom: 6 }}>Notes (optional)</div>
                    <Input.TextArea rows={2} placeholder="Reason for nomination or special considerations..."
                        value={nomNotes} onChange={e => setNomNotes(e.target.value)} />
                </div>
            </Modal>
        </div>
    );
}
